<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { loginUser } from '../../../../components/auth/LoginUser';
  import Loading from '../../../../etc/loader.svelte';

  import RepositoryFactory, { SUB_CONTRACT_HIKARI01_TEL } from '../../../../repositories/RepositoryFactory';
  import type { SubContractHikari01TelResult } from '../../../../repositories/subcontracthikari01tel';
  import TerminateGateCallDetail from './TerminateGateCallDetail.svelte';
  import { getDate } from 'date-fns';
  import type { IGateCallItem } from './types';

  export let subContractId!: number;

  const errorProcess = (error: any) => {
    if (error.response.status === 403) {
      alert('セッションが切れました。再度ログインしてください。');
      loginUser.logout();
      push('/login');
    } else {
      alert('エラーが発生しました。');
    }
  };

  let pageStatus: 'TOP' | 'TERMINATE_GATE_CALL' = 'TOP';
  let hikari01Tel: SubContractHikari01TelResult;
  let gateCallItem: IGateCallItem;
  let promise = initialize();

  /** 初期表示時処理 */
  async function initialize() {
    const repository = RepositoryFactory[SUB_CONTRACT_HIKARI01_TEL];
    repository
      .fetch({ subContractId })
      .then((response) => {
        hikari01Tel = response;
        setValuesToGateCallItem();
      })
      .catch((error) => errorProcess(error));

    return { hikari01Tel };
  }

  const setValuesToGateCallItem = () => {
    gateCallItem = {
      contractName: hikari01Tel?.telServiceName,
      telNo: hikari01Tel?.hikari01TelNumber,
      contractStartDate: hikari01Tel?.contractStartDate,
      telServiceCode: hikari01Tel?.telServiceCode
    };
  };

  const handleTransitionTerminateGateCall = (): void => {
    pageStatus = 'TERMINATE_GATE_CALL';
  };

  const isTerminateOutSideThePeriod = () => {
    const currentDate: number = getDate(new Date());
    return !(1 <= currentDate && currentDate <= 20);
  };
</script>

{#await promise}
  <Loading />
{:then}
  <div class="h-auto p-6 bg-white m-auto max-w-4xl">
    <div class="mb-6">
      <div class="container flex justify-between">
        <h2 class="text-xl font-bold text-gray-700">付帯契約(電話)の手続き</h2>
      </div>
    </div>
    {#if isTerminateOutSideThePeriod()}<!-- 解約可能期間外 -->
      <div class="shadow rounded bg-white border-t-2 border-green-400">
        <div class="m-10">
          <div class="inline-flex justify-items-center items-center min-w-full mb-10">
            <div class="mb:font-sm w-full">
              <ul class="list-none">
                <li>
                  <p>でんわ契約の解約は毎月20日までとなっております。</p>
                </li>
                <li>
                  <p>今月の解約は締め切りましたので、次月1日以降20日までにお手続きいただけますようお願いします。</p>
                </li>
                <li class="mt-10">
                  <p>お手続きについてご不明な点がございましたら、以下からご連絡いただくようお願いいたします。</p>
                </li>
                <li>
                  <p>URL：https://bb01.unext.co.jp/contact</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    {:else if pageStatus === 'TOP'}<!-- トップ画面 -->
      <div class="shadow rounded bg-white border-t-2 border-green-400">
        <div class="mx-auto mt-10 w-9/12 mb:font-sm">
          <div class="shadow rounded bg-gray-50 w-full p-6">
            <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
              <div class="grid-1 justify-self-start w-full">
                <span class="label-title">契約中の電話契約</span>
              </div>
              <div class="grid-1 justify-self-start w-full">
                <p class="label-value">{gateCallItem?.contractName}</p>
              </div>
            </div>

            <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
              <div class="grid-1 justify-self-start w-full">
                <span class="label-title">電話番号</span>
              </div>
              <div class="grid-1 justify-self-start w-full">
                <p class="label-value">{gateCallItem?.telNo}</p>
              </div>
            </div>

            <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
              <div class="grid-1 justify-self-start w-full">
                <span class="label-title">契約開始日</span>
              </div>
              <div class="grid-1 justify-self-start w-full">
                <p class="label-value">{gateCallItem?.contractStartDate}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="m-10">
          <div class="mt-28">
            <h3 class="text-lg font-bold">付帯契約(電話)の解約</h3>

            <div class="flex flex-col divide-y divide-gray-300 py-4">
              <!-- [Note] divideの線をだすためにからのdivタグを入れています -->
              <div class="flex-1" />
              <div class="inline-grid grid-cols-3 justify-items-center items-center py-4">
                <div class="grid-1 col-span-2 justify-self-start">
                  <span class="inline-block">ご契約中の電話の解約をする場合はこちら</span>
                </div>
                <div class="grid-1 justify-self-end">
                  <button on:click={handleTransitionTerminateGateCall} class="button-primary"> 手続きへ </button>
                </div>
              </div>
              <div class="flex-1" />
            </div>
          </div>
        </div>
      </div>
    {:else if pageStatus === 'TERMINATE_GATE_CALL'}<!-- GateCall 解約 -->
      <TerminateGateCallDetail {gateCallItem} />
    {/if}
  </div>
{/await}

<style lang="postcss">
  p:not([class]) {
    @apply tracking-normal mb-1;
  }
  .label-title {
    @apply text-sm font-medium text-gray-700;
  }
  .label-value {
    @apply text-sm font-medium text-gray-900;
  }
  .button-primary {
    @apply bg-green-400 rounded hover:bg-green-500 text-sm font-semibold text-white focus:outline-none px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default;
  }
</style>
